import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useKPIs } from '../contexts/KPIContext';
import { usePreventionModel } from '../contexts/PreventionModelContext';
function TrendSummary({
  label,
  current,
  baseline,
  unit,
  color
}: {
  label: string;
  current: number;
  baseline: number;
  unit: string;
  color: string;
}) {
  const diff = current - baseline;
  const pctChange = baseline !== 0 ? Math.abs(diff / baseline * 100).toFixed(0) : '—';
  const isUp = diff > 0;
  const isFlat = diff === 0;
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  return <span className="inline-flex items-center gap-1.5 text-xs" data-id="element-958" data-mp="2byk5ifh9xhi6">
      <span className="font-medium text-gray-600" data-id="element-959" data-mp="2gxwsr1c24qla">{label}</span>
      <span className={`inline-flex items-center gap-0.5 font-semibold ${color}`} data-id="element-960" data-mp="2gzhnaqydg9j5">
        <Icon size={12} data-id="element-961" data-mp="twivv6rpk4e3" />
        {isFlat ? 'Flat' : `${isUp ? '+' : '−'}${pctChange}%`}
      </span>
      <span className="text-gray-400" data-id="element-962" data-mp="oyh6k43ov8u6">
        ({current}
        {unit})
      </span>
    </span>;
}
export function KPITimelineChart({
  useNewModel = false
}: {
  useNewModel?: boolean;
}) {
  const oldContext = useKPIs();
  const newContext = usePreventionModel();
  const [expanded, setExpanded] = useState(false);
  // Get data depending on which context we're using
  let leadingData: any = null;
  let laggingData: any = null;
  if (useNewModel) {
    const {
      indicators
    } = newContext;
    leadingData = indicators.find(i => i.type === 'leading' && i.history.length > 0);
    laggingData = indicators.find(i => i.type === 'lagging' && i.history.length > 0);
  } else {
    const {
      kpis
    } = oldContext;
    leadingData = kpis.find(k => k.type === 'leading' && k.history.length > 0);
    laggingData = kpis.find(k => k.type === 'lagging' && k.history.length > 0);
  }
  if (!leadingData || !laggingData) {
    return null;
  }
  const chartData = leadingData.history.map((lh: any, index: number) => {
    const lagH = laggingData.history[index] || laggingData.history[laggingData.history.length - 1];
    return {
      date: lh.date,
      leadingValue: lh.value,
      laggingValue: lagH ? lagH.value : null
    };
  });
  return <div className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden" data-id="element-963" data-mp="2hcfde9jh6nzd">
      {/* Compact header — always visible */}
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors" data-id="element-964" data-mp="2mapawqsoa0o5">
        <div className="flex items-center gap-4" data-id="element-965" data-mp="2w9euco07vt6i">
          <h3 className="text-xs font-semibold text-gray-900" data-id="element-966" data-mp="wxlu8ii4u7pk">
            Indicator Trends
          </h3>
          <div className="flex items-center gap-3 border-l border-gray-200 pl-4" data-id="element-967" data-mp="11wf3tzuatekg">
            <TrendSummary label={useNewModel ? leadingData.name : leadingData.metric} current={leadingData.currentValue} baseline={useNewModel ? leadingData.history[0]?.value || 0 : leadingData.baselineValue} unit={leadingData.unit} color="text-indigo-600" data-id="element-968" data-mp="3f6yg94m0ikux" />
            <span className="text-gray-300" data-id="element-969" data-mp="206h3jj7zz7g">·</span>
            <TrendSummary label={useNewModel ? laggingData.name : laggingData.metric} current={laggingData.currentValue} baseline={useNewModel ? laggingData.history[0]?.value || 0 : laggingData.baselineValue} unit={laggingData.unit} color="text-amber-600" data-id="element-970" data-mp="1zwpxsmfvm06" />
          </div>
        </div>
        <motion.div animate={{
        rotate: expanded ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-971" data-mp="28qw64lkod02e">
          <ChevronDown size={16} className="text-gray-400" data-id="element-972" data-mp="gwsdy268ta9" />
        </motion.div>
      </button>

      {/* Expandable chart */}
      <AnimatePresence data-id="element-973" data-mp="5ar5ujr6oc0w">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-974" data-mp="3nnyxcioo5k2v">
            <div className="px-4 pb-3" data-id="element-975" data-mp="aadpbaxluoi5">
              <div className="h-[140px] w-full" data-id="element-976" data-mp="f61bdhgsj1jf">
                <ResponsiveContainer width="100%" height="100%" data-id="element-977" data-mp="f8xa380gpu8x">
                  <LineChart data={chartData} margin={{
                top: 5,
                right: 10,
                left: 0,
                bottom: 0
              }} data-id="element-978" data-mp="2uxgpz5gwwwjn">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-979" data-mp="2nf8vxswgg6i4" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} data-id="element-980" data-mp="2wfe0zwe6est4" />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} width={35} data-id="element-981" data-mp="2ifmj3kkjgs9u" />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} width={35} data-id="element-982" data-mp="1l10i75ya53jy" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  fontSize: '12px'
                }} data-id="element-983" data-mp="3lqndhsatbsa" />
                    <Line yAxisId="left" type="monotone" dataKey="leadingValue" name={useNewModel ? leadingData.name : leadingData.title} stroke="#4f46e5" strokeWidth={2} dot={{
                  r: 3,
                  strokeWidth: 2
                }} activeDot={{
                  r: 5
                }} data-id="element-984" data-mp="3c1aadz2dlxyw" />
                    <Line yAxisId="right" type="monotone" dataKey="laggingValue" name={useNewModel ? laggingData.name : laggingData.title} stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3" dot={{
                  r: 3,
                  strokeWidth: 2
                }} activeDot={{
                  r: 5
                }} data-id="element-985" data-mp="2bu56n7rrwjg8" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 mt-1" data-id="element-986" data-mp="3iibvrjyavy0a">
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500" data-id="element-987" data-mp="3dj8odsuulfp5">
                  <span className="w-4 h-0.5 bg-indigo-500 rounded-full inline-block" data-id="element-988" data-mp="2mdmug7482fp1" />
                  {useNewModel ? leadingData.name : leadingData.title}
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500" data-id="element-989" data-mp="2resn64xvu2yk">
                  <span className="w-4 h-0.5 bg-amber-500 rounded-full inline-block border-dashed" style={{
                borderTop: '2px dashed #f59e0b',
                height: 0,
                background: 'none'
              }} data-id="element-990" data-mp="2cnjs3gb7npe1" />
                  {useNewModel ? laggingData.name : laggingData.title}
                </span>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-1" data-id="element-991" data-mp="1yo0ydsj5cgl9">
                Alignment does not imply causation.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}